<template>
  <div class="admin-hero-slides">
    <!-- Header -->
    <div class="admin-header">
      <h1 class="admin-title">
        <i class="fas fa-images"></i>
        Hero Slider Yönetimi
      </h1>
      <button @click="openCreateModal" class="btn btn-create">
        <i class="fas fa-plus"></i>
        Yeni Slide Ekle
      </button>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-images"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ totalSlides }}</div>
          <div class="stat-label">Toplam Slide</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <i class="fas fa-eye"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ activeSlides }}</div>
          <div class="stat-label">Aktif Slide</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="admin-filters">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input 
          v-model="searchQuery" 
          @input="debouncedSearch"
          type="text" 
          placeholder="Slide başlığı ile ara..."
        >
      </div>
      <div class="filter-actions">
        <select v-model="filterActive" @change="loadSlides">
          <option value="">Tümü</option>
          <option value="1">Aktif</option>
          <option value="0">Pasif</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Slides yükleniyor...</p>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && slides.length === 0" class="empty-state">
      <i class="fas fa-images"></i>
      <h3>Henüz slide eklenmemiş</h3>
      <p>İlk slide'ınızı eklemek için "Yeni Slide Ekle" butonuna tıklayın.</p>
    </div>

    <!-- Slides Grid -->
    <div v-if="!loading && slides.length > 0" class="admin-slides-grid">
      <div 
        v-for="slide in slides" 
        :key="slide.id"
        class="slide-card"
      >
        <!-- Slide Image -->
        <div class="slide-image">
          <img 
            v-if="slide.image_url" 
            :src="slide.image_url" 
            :alt="slide.title_tr"
            @error="handleImageError"
          />
          <div v-else class="slide-placeholder">
            <i class="fas fa-image"></i>
          </div>
        </div>

        <!-- Slide Content -->
        <div class="slide-content">
          <div class="slide-header">
            <h3 class="slide-title">{{ slide.title_tr }}</h3>
            <div class="slide-actions">
              <button 
                @click="toggleActive(slide)" 
                :class="['btn-status', { active: parseInt(slide.is_active) === 1 }]"
                :title="parseInt(slide.is_active) === 1 ? 'Pasif Yap' : 'Aktif Yap'"
              >
                <i :class="parseInt(slide.is_active) === 1 ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
              </button>
              <button @click="openEditModal(slide)" class="btn-edit" title="Düzenle">
                <i class="fas fa-edit"></i>
              </button>
              <button @click="deleteSlide(slide)" class="btn-delete" title="Sil">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>

          <p class="slide-title-en">{{ slide.title_en }}</p>
          <p class="slide-description">{{ truncateText(slide.description_tr, 100) }}</p>
          
          <div class="slide-meta">
            <span class="slide-order">Sıra: {{ slide.sort_order }}</span>
            <span class="slide-button">{{ slide.button_text_tr || 'Daha Fazla Bilgi' }}</span>
            <span :class="['slide-status', { active: parseInt(slide.is_active) === 1 }]">
              {{ parseInt(slide.is_active) === 1 ? 'Aktif' : 'Pasif' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Slide Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ isEditing ? 'Slide Düzenle' : 'Yeni Slide Ekle' }}</h2>
          <button @click="closeModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <form @submit.prevent="submitForm" class="modal-form">
          <div class="form-row">
            <div class="form-group">
              <label>Başlık (Türkçe) *</label>
              <input 
                v-model="form.title_tr" 
                type="text" 
                required 
                placeholder="Slide başlığı (Türkçe)"
              />
            </div>
            <div class="form-group">
              <label>Başlık (İngilizce) *</label>
              <input 
                v-model="form.title_en" 
                type="text" 
                required 
                placeholder="Slide title (English)"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Açıklama (Türkçe) *</label>
              <textarea 
                v-model="form.description_tr" 
                required 
                rows="4"
                placeholder="Slide açıklaması (Türkçe)"
              ></textarea>
            </div>
            <div class="form-group">
              <label>Açıklama (İngilizce) *</label>
              <textarea 
                v-model="form.description_en" 
                required 
                rows="4"
                placeholder="Slide description (English)"
              ></textarea>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Buton Metni (Türkçe)</label>
              <input 
                v-model="form.button_text_tr" 
                type="text" 
                placeholder="Daha Fazla Bilgi"
              />
            </div>
            <div class="form-group">
              <label>Buton Metni (İngilizce)</label>
              <input 
                v-model="form.button_text_en" 
                type="text" 
                placeholder="Learn More"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Buton Aksiyonu</label>
              <select v-model="form.button_action">
                <option value="contact">İletişim</option>
                <option value="projects">Projeler</option>
                <option value="services">Hizmetler</option>
                <option value="about">Hakkımızda</option>
              </select>
            </div>
            <div class="form-group">
              <label>Görsel URL *</label>
              <input 
                v-model="form.image_url" 
                type="url" 
                required
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Sıra</label>
              <input 
                v-model.number="form.sort_order" 
                type="number" 
                min="0"
                placeholder="0"
              />
            </div>
            <div class="form-group">
              <label class="checkbox-group">
                <input 
                  v-model="form.is_active" 
                  type="checkbox"
                />
                <span>Aktif</span>
              </label>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModal" class="btn btn-cancel">
              İptal
            </button>
            <button type="submit" :disabled="submitting" class="btn btn-submit">
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              {{ submitting ? 'Kaydediliyor...' : (isEditing ? 'Güncelle' : 'Ekle') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { adminApi } from '@/services/adminApi'

export default {
  name: 'AdminHeroSlides',
  emits: ['statsUpdated'],
  setup(props, { emit }) {
    // Reactive Data
    const slides = ref([])
    const loading = ref(false)
    const submitting = ref(false)
    const showModal = ref(false)
    const isEditing = ref(false)
    const searchQuery = ref('')
    const filterActive = ref('')

    // Form Data
    const form = reactive({
      id: null,
      title_tr: '',
      title_en: '',
      description_tr: '',
      description_en: '',
      button_text_tr: '',
      button_text_en: '',
      button_action: 'contact',
      image_url: '',
      sort_order: 0,
      is_active: true
    })

    // Reactive stats
    const totalSlides = ref(0)
    const activeSlides = ref(0)
    
    // Update stats method
    const updateStats = async () => {
      try {
        // Toplam slide sayısı için tüm slide'ları getir
        const totalResponse = await adminApi.getHeroSlides({})
        if (totalResponse && totalResponse.success) {
          totalSlides.value = totalResponse.data ? totalResponse.data.length : 0
        }
        
        // Aktif slide sayısı için sadece aktif olanları getir
        const activeResponse = await adminApi.getHeroSlides({ active: 'true' })
        if (activeResponse && activeResponse.success) {
          activeSlides.value = activeResponse.data ? activeResponse.data.length : 0
        }
        
        // Stats'ı parent'a bildir
        emit('statsUpdated', {
          total: totalSlides.value,
          active: activeSlides.value
        })
        
        console.log('Stats updated:', { total: totalSlides.value, active: activeSlides.value })
      } catch (error) {
        console.error('Stats güncellenemedi:', error)
      }
    }

    // Methods
    const loadSlides = async () => {
      try {
        loading.value = true
        const params = {}
        
        // Admin için filtreleme - boş string ise tümünü getir
        if (filterActive.value !== '') {
          params.active = filterActive.value === '1' ? 'true' : 'false'
        }
        // filterActive.value === '' ise params.active eklenmez (tüm slide'lar)

        console.log('Loading slides with filterActive.value:', filterActive.value)
        console.log('Loading slides with params:', params)
        const response = await adminApi.getHeroSlides(params)
        console.log('Hero slides API response:', response)
        
        if (response && response.success) {
          slides.value = response.data || []
          console.log('Loaded slides count:', slides.value.length)
          console.log('Slide details:')
          slides.value.forEach((slide, index) => {
            console.log(`  Slide ${index}: id=${slide.id}, title=${slide.title_tr}, is_active=${slide.is_active}`)
          })
          
          // İstatistikler için ayrı API çağrıları yap
          await updateStats()
        } else {
          console.error('API response unsuccessful:', response)
          slides.value = []
        }
      } catch (error) {
        console.error('Slides yüklenemedi:', error)
        console.error('Error details:', error.response?.data)
        slides.value = []
      } finally {
        loading.value = false
      }
    }

    const openCreateModal = () => {
      isEditing.value = false
      resetForm()
      showModal.value = true
    }

    const openEditModal = (slide) => {
      isEditing.value = true
      Object.assign(form, {
        id: slide.id,
        title_tr: slide.title_tr,
        title_en: slide.title_en,
        description_tr: slide.description_tr,
        description_en: slide.description_en,
        button_text_tr: slide.button_text_tr || '',
        button_text_en: slide.button_text_en || '',
        button_action: slide.button_action || 'contact',
        image_url: slide.image_url || '',
        sort_order: slide.sort_order || 0,
        is_active: parseInt(slide.is_active) === 1
      })
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      resetForm()
    }

    const resetForm = () => {
      Object.assign(form, {
        id: null,
        title_tr: '',
        title_en: '',
        description_tr: '',
        description_en: '',
        button_text_tr: '',
        button_text_en: '',
        button_action: 'contact',
        image_url: '',
        sort_order: 0,
        is_active: true
      })
    }

    const submitForm = async () => {
      try {
        submitting.value = true

        const slideData = {
          title_tr: form.title_tr,
          title_en: form.title_en,
          description_tr: form.description_tr,
          description_en: form.description_en,
          button_text_tr: form.button_text_tr,
          button_text_en: form.button_text_en,
          button_action: form.button_action,
          image_url: form.image_url,
          sort_order: form.sort_order,
          is_active: form.is_active
        }

        if (isEditing.value) {
          await adminApi.updateHeroSlide(form.id, slideData)
        } else {
          await adminApi.createHeroSlide(slideData)
        }

        closeModal()
        await loadSlides()
        await updateStats()
      } catch (error) {
        console.error('Slide kaydedilemedi:', error)
        alert('Slide kaydedilemedi. Lütfen tekrar deneyin.')
      } finally {
        submitting.value = false
      }
    }

    const deleteSlide = async (slide) => {
      if (!confirm(`"${slide.title_tr}" slide'ını silmek istediğinize emin misiniz?`)) {
        return
      }

      try {
        await adminApi.deleteHeroSlide(slide.id)
        await loadSlides()
        await updateStats()
      } catch (error) {
        console.error('Slide silinemedi:', error)
        alert('Slide silinemedi. Lütfen tekrar deneyin.')
      }
    }

    const toggleActive = async (slide) => {
      try {
        const currentStatus = parseInt(slide.is_active)
        const newStatus = currentStatus === 1 ? 0 : 1
        
        console.log('Toggling slide status:', {
          slideId: slide.id,
          currentStatus,
          newStatus,
          slideData: slide
        })
        
        const response = await adminApi.updateHeroSlide(slide.id, { is_active: newStatus })
        console.log('Toggle response:', response)
        
        if (response && response.success) {
          slide.is_active = newStatus
          // Stats'ı güncelle
          await updateStats()
          console.log('Slide status updated successfully')
        } else {
          console.error('API response unsuccessful:', response)
          alert('Slide durumu güncellenemedi. API yanıtı başarısız.')
        }
      } catch (error) {
        console.error('Slide durumu güncellenemedi:', error)
        console.error('Error details:', error.response?.data)
        alert('Slide durumu güncellenemedi. Lütfen tekrar deneyin.')
      }
    }

    const truncateText = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }

    const handleImageError = (event) => {
      event.target.style.display = 'none'
    }

    // Search with debounce
    let searchTimeout
    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        // Filter slides locally for search
        // In a real app, you might want to send search query to API
        loadSlides()
      }, 300)
    }

    // Lifecycle
    onMounted(async () => {
      await loadSlides()
      await updateStats()
    })

    return {
      // Data
      slides,
      loading,
      submitting,
      showModal,
      isEditing,
      searchQuery,
      filterActive,
      form,
      
      // Computed
      totalSlides,
      activeSlides,
      
      // Methods
      loadSlides,
      updateStats,
      openCreateModal,
      openEditModal,
      closeModal,
      submitForm,
      deleteSlide,
      toggleActive,
      truncateText,
      handleImageError,
      debouncedSearch
    }
  }
}
</script>

<style scoped>
.admin-hero-slides {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;
}

.admin-title {
  font-size: 28px;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.admin-title i {
  color: #e74c3c;
}

/* Statistics Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  background: #e74c3c;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.stat-icon.active {
  background: #27ae60;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
}

/* Filters - same as AdminServices */
.admin-filters {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  align-items: center;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.search-box input {
  width: 100%;
  padding: 12px 12px 12px 45px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.search-box input:focus {
  outline: none;
  border-color: #e74c3c;
}

.filter-actions {
  display: flex;
  gap: 15px;
}

.filter-actions select {
  padding: 12px 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.filter-actions select:focus {
  outline: none;
  border-color: #e74c3c;
}

/* Loading & Empty States - same as AdminServices */
.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 48px;
  color: #ddd;
  margin-bottom: 20px;
}

/* Slides Grid */
.admin-slides-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.slide-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.slide-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.slide-image {
  height: 200px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.slide-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-placeholder {
  font-size: 48px;
  color: #e74c3c;
}

.slide-content {
  padding: 20px;
}

.slide-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.slide-title {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin: 0;
  flex: 1;
}

.slide-title-en {
  font-size: 14px;
  color: #7f8c8d;
  margin: 5px 0 10px 0;
  font-style: italic;
}

.slide-description {
  color: #5a6c7d;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 15px;
}

.slide-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #7f8c8d;
  flex-wrap: wrap;
  gap: 8px;
}

.slide-button {
  background: #e74c3c;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
}

.slide-status {
  padding: 4px 8px;
  border-radius: 4px;
  background: #e74c3c;
  color: white;
  font-size: 11px;
  font-weight: bold;
}

.slide-status.active {
  background: #27ae60;
}

.slide-actions {
  display: flex;
  gap: 5px;
}

.btn-status, .btn-edit, .btn-delete {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 12px;
}

.btn-status {
  background: #95a5a6;
  color: white;
}

.btn-status.active {
  background: #27ae60;
}

.btn-edit {
  background: #3498db;
  color: white;
}

.btn-delete {
  background: #e74c3c;
  color: white;
}

.btn-status:hover, .btn-edit:hover, .btn-delete:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

/* Buttons - same as AdminServices */
.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-create {
  background: #27ae60;
  color: white;
}

.btn-create:hover {
  background: #219a52;
}

.btn-submit {
  background: #3498db;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background: #2980b9;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background: #95a5a6;
  color: white;
}

.btn-cancel:hover {
  background: #7f8c8d;
}

/* Modal Styles - same as AdminServices */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #7f8c8d;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.modal-close:hover {
  background: #f8f9fa;
}

.modal-form {
  padding: 30px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #e74c3c;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.checkbox-group input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-hero-slides {
    padding: 15px;
  }
  
  .admin-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .admin-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    max-width: none;
  }
  
  .admin-slides-grid {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal-content {
    margin: 10px;
  }
  
  .modal-form {
    padding: 20px;
  }
}
</style>